//#region [IMPORT]

// === THIRD-PARTY ===
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { useTheme } from '@mui/material/styles';
import {
  Box,
  CardHeader,
  Typography
} from '@mui/material';

// === CONSTANTS ===
import { FAILURE_CODE, SUCCESS_CODE } from "constants/general";

// === COMPONENTS ===
import { Spacer } from "components/Spacer";
import { BorderCorners } from "components/Border/components/BorderCorners";
import { TaskContext } from "pages/TaskVerification/components/TaskContext/TaskContext";
import { Pagetype , validate as validate_pagetype  } from './components/Pagetype/Pagetype';
import { Doctype  , validate as validate_doctype   } from './components/Doctype/Doctype';
import { Standard , validate as validate_standard  } from './components/Standard/Standard';
import { Selection, validate as validate_selection } from './components/Selection/Selection';
import { Amount   , validate as validate_amount    } from './components/Amount/Amount';
import { Integer  , validate as validate_integer   } from "./components/Integer/Integer";
import { Date     , validate as validate_date      } from './components/Date/Date';
import { MIN      , validate as validate_min       } from './components/MIN/MIN';

// === HOOKS ===
import useOnVisible from "components/hooks/useOnVisible/useOnVisible";

// === PRESENTATION ===
import { getFieldBorderColor } from './fieldPresentation';
import { displayRotation, entryImageRect, fillCanvasBackground, rotatedDimensions, rotationComponents } from '../../Image/Canvas/imageGeometry';

// === API ===
import { addTableRow, isFocusedField, onField, onFieldFocus, removeListener, removeTableRow, requestField, requestPreviousFieldFocus, setMainField, updateField, requestPageUp, requestPageDown,  requestThumbNavigation } from "api/prokey/prokey.websocket";


//#endregion

//-----------------------------------------------------------------
export function Field({id, timestamp}) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const theme = useTheme();
  const { images, images_revision } = useContext( TaskContext );

  const [ data        , setData        ] = useState( {} );
  const [ message     , setMessage     ] = useState( '' );
  const [ reference   , setReference   ] = useState( null );
  const [ has_focus   , setHasFocus    ] = useState( isFocusedField( id ) );
  const focusByPointerRef = useRef( false );

  const ref_outer = useRef();
  const [ is_in_view ] = useOnVisible( ref_outer );

  // === SNIPPETS
  const setting_snippets_enabled = toBoolean( localStorage.getItem( 'setting-snippets-enabled' ) || false );
  const setting_snippets_animate = toBoolean( localStorage.getItem( 'setting-snippets-animate' ) || true  );
  const should_create_snippets = setting_snippets_enabled && (is_in_view || has_focus);
  const snippets = useMemo( createSnippets, [data?.Entries, data?.image, images, images_revision, should_create_snippets] );

  const header = data.DisplayInfo ? data.DisplayInfo.DisplayHeader : '' ;
  const length_char = data.DisplayInfo ? +data.DisplayInfo.Length : 50;
  const length = data.DisplayInfo ? Math.max(length_char * 12, 20 )+'px' : '100%' ;
  const alt_content       = data.DisplayInfo ? data.DisplayInfo.AltContent      || [] : [];
  const cross_doc_content = data.DisplayInfo ? data.DisplayInfo.CrossDocContent || [] : [];
  const selection_values  = [ ...alt_content, ...cross_doc_content ];
  const casing = data.DisplayInfo?.is_uppercase ? 'uppercase' : data.DisplayInfo?.is_lowercase ? 'lowercase' : '';

  const is_validated = data.validationInfo?.isRejected || data.validationInfo?.isAccepted;
  const border_color = getFieldBorderColor(data, theme.palette);
  const input_border_color = `${border_color} !important`;

  const is_visible = data.is_visible;
  let input_validation = () => { return false };

  const setRefNode = useCallback(node => {
    if (node !== null) {
      setReference( {current: node} );
    }
  }, []);

  //#endregion

  //#region [STYLES]

  const _styles = {
    container: {
      display   : !data.Id || is_visible ? '' : 'none',
      padding   : '1rem',
      flex      : 'auto',
      maxWidth  : '-webkit-fill-available',
      maxWidth  : '100%',
      minHeight : '30px',
    },
    header: {
      padding: 0,
      paddingBottom: '0.5rem',
    },
    page_number:{
      paddingRight    : '1rem',     
      fontSize        : '17px',
      marginTop       : '0.7rem',
    },
    content: {
      width  : '100%',
    },
    input: {
      borderRadius    : '5px',
      borderStyle     : 'solid',
      borderColor     : input_border_color,
      fontSize        : '15px',
      width           : length,
      height          : '36px',
      textTransform   : casing,
      paddingLeft     : '8px',
      maxWidth        : '-webkit-fill-available',
      maxWidth        : '100%',
      '&.Mui-focused': {
        borderColor: input_border_color,
      },
      '&:focus': {
        borderColor: input_border_color,
        outline: 'none',
      },
      '& input:focus': {
        outline: 'none',
      },
    },
    input_content: {
      border          : 'none',
      borderRadius    : 0,
      boxSizing       : 'border-box',
      fontSize        : '15px',
      height          : '100%',
      padding         : 0,
      textTransform   : casing,
      width           : '100%',
      '&:focus': {
        outline: 'none',
      },
    },
  }

  const _props = {
    container: {
      sx: _styles.container,
      onPointerDownCapture: () => { focusByPointerRef.current = true; },
      onClick: () => {
        focusByPointerRef.current = false;
        setMainField(data);
      },
    },
    header: {
      sx: _styles.header,
      title: header,      
    },
    content: {
      sx: _styles.content,      
    },
  }

  //#endregion

  //#region [EFFECT]

  useEffect( () => {
    //-----------------------------------------------------------------
    function handleField(data) {
      //-----------------------------------------------------------------
      setData(data);
      setMessage( data.DisplayInfo?.Message || '' );
    }

    onField( id, handleField );

    return () => {
      removeListener( 'FIELD-' + id, handleField )
    }
  }, [id] );

  useEffect( () => {
    //-----------------------------------------------------------------
    function handleFieldFocus(data) {
      //-----------------------------------------------------------------
      if ( data?.clear ) {
        setHasFocus( false );
        return;
      }

      if ( data ) {
        setData( data );
        setMessage( getFocusMessage(data) );
      }
      setHasFocus( true );

      if( !is_in_view && ref_outer.current ) {
        ref_outer.current.scrollIntoView({ behavior: "instant", block: "center", inline: "nearest" });
      }
      
      if( reference?.current ) {
        reference.current.scrollIntoView({ behavior: "auto", block: "center", inline: "nearest" });
      }
      else {
        return;
      }

      const alreadyFocused = document.activeElement === reference?.current;

      // If user clicked into the field, don't override their cursor position
      if (!alreadyFocused) {
        reference?.current.focus();

        if (reference.current.select) {
          reference?.current.select();
        }
      }
    }

    onFieldFocus( id, handleFieldFocus );

    return () => {
      removeListener( 'FIELD-FOCUS-' + id, handleFieldFocus )
    }
  }, [reference,ref_outer,is_in_view] );

  useEffect( () => {
    // Initial focus can be received before the field mounts. Requesting the
    // retained focused field lets the server replay FIELD-FOCUS after loading.
    if(!is_in_view && !has_focus) return;
    requestField( id );
  }, [id, is_in_view, timestamp, has_focus] );

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onFocus() {
    //-----------------------------------------------------------------
    try {
      setMessage(getFocusMessage(data));
      const is_pointer_focus = focusByPointerRef.current;
      focusByPointerRef.current = false;

      if( !is_pointer_focus && reference?.current?.select ) {
        reference.current.select();
      }
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function onBlur() {
    //-----------------------------------------------------------------
    try {
      // Browser blur should not hide the selected field's validation message.
      // Field focus is cleared by FIELD-FOCUS clear events when another field is selected.
    } catch (error) {
      console.warn(error);
    }
  }
  //-----------------------------------------------------------------
  function toBoolean(str) {
    //-----------------------------------------------------------------
    if( str === true ) return true;
    const enabled = ['1', 'true', 'yes', 'ja', 'on'];
    return str && enabled.includes(str.toLowerCase());
  }
  
  //-----------------------------------------------------------------
  function onKeyDown( event ) {
    //-----------------------------------------------------------------
    // Allow Enter events to propagate to child input components.

    const is_action_prev = event.key === 'Tab' && event.shiftKey ;
    if( is_action_prev ) {
      event.preventDefault();
      event.stopPropagation();
      requestPreviousFieldFocus( id );
      return;
    }
    
    const is_action_tab = event.key === 'Tab'   && !event.shiftKey ;

    const is_action_page_up   = event.key === 'PageUp';
    const is_action_page_down = event.key === 'PageDown';

    if ( is_action_tab ) {
      // Stop default tab behavior
      event.preventDefault();
    }

    if( is_action_page_up ) {
      event.preventDefault();    
      requestThumbNavigation('prev');
      return;
    }

    if( is_action_page_down ) {
      event.preventDefault();
      requestThumbNavigation('next');      
      return;
    }

    // === TABLE COMMANDS ===
    if( data.isColumn ) {
      const is_action_create_row_above = event.key === 'ArrowUp'   && event.ctrlKey;
      const is_action_create_row_below = event.key === 'ArrowDown' && event.ctrlKey;
      const is_action_remove_row       = event.key === 'Delete'    && event.ctrlKey;
      if( is_action_create_row_above || is_action_create_row_below) {
        addTableRow( { field_id: id, isAfter: is_action_create_row_below } );
        event.preventDefault();
        return;
      }
      if( is_action_remove_row ) {
        removeTableRow( { field_id: id } );
        event.preventDefault();
        return;
      }
    }
  }
  
  //-----------------------------------------------------------------
  function confirm( actions={}, value ) {
    //-----------------------------------------------------------------
    const {
      is_action_force, is_action_enter, is_action_tab,
    } = actions;

    const ref_content = value;

    const force_accept    = data?.validationInfo?.ForceAccept;
    const is_force_empty  = ref_content === '' && force_accept?.toLowerCase() === 'empty';
    const is_force_filled = ref_content !== '' && force_accept?.toLowerCase() === 'filled';
    const is_force_never  = force_accept?.toLowerCase() === 'never';
    const is_force_always = force_accept?.toLowerCase() === 'always';   
    const is_forcable     = !is_force_never && (is_force_always || is_force_empty || is_force_filled);
    const is_forced       = is_action_force && is_forcable;
 
    const false_accept      = data?.validationInfo?.FalseAccept;
    const is_false_filled   = ref_content === '' && false_accept?.toLowerCase() === 'filled';
    const is_false_never    = ref_content === '' && false_accept?.toLowerCase() === 'never';
    const is_false_rejected = is_false_filled || is_false_never;

    const is_accepted     = is_action_force || is_action_enter || is_action_tab;

    const validation_result_common = validate_common( ref_content );
    const validation_result = is_accepted ? input_validation( ref_content, data.validationInfo, data.DisplayInfo.Type ) : {};
    const is_valid = validation_result_common?.status === SUCCESS_CODE && validation_result?.status === SUCCESS_CODE;

    const has_coords         = data.Entries[0].Bottom > 0;
    const is_coords_required = toBoolean( data?.validationInfo?.RequireCoords );
    const is_coords_failed   = is_coords_required && !has_coords;

    const is_forced_version = is_valid && is_action_force && data.Name === 'Version';

    function rejectField(message, use_configured_message = true) {
      const configured_message = use_configured_message
        ? data.DisplayInfo?.ErrorMessage || message
        : message;
      setMessage( configured_message );
      setData((current) => {
        const next = current?.Id === data.Id ? current : data;
        return {
          ...next,
          DisplayInfo: {
            ...next.DisplayInfo,
            Message: configured_message,
          },
          validationInfo: {
            ...next.validationInfo,
            isAccepted: false,
            isRejected: true,
          },
        };
      });
    }

    // === VALIDATION ===
    if( is_forced || is_forced_version) {
      setMessage('Field has been force accepted!');

      updateField( 'FORCE', id, data.Name, { Content: validation_result.content } );
      return { status: SUCCESS_CODE, content: validation_result.content };
    }

    if( is_accepted && ref_content !== '' && is_coords_failed ) {
      rejectField( 'Field requires coordinates!', false );
      return { status: FAILURE_CODE, content: validation_result.content };
    }
    
    if( is_accepted && is_false_rejected ) {
      let message = 'Enter a value.'
      if( is_forcable ) message += ' [Field can be forced]';
      rejectField( message );
      return { status: FAILURE_CODE, content: validation_result.content };
    }

    if( is_accepted && !is_valid ) {
      const validation_message = validation_result_common?.status === FAILURE_CODE
        ? validation_result_common?.message
        : validation_result?.message
      rejectField( validation_message, false );
      return { status: FAILURE_CODE, content: validation_result.content };
    }

    if ( is_action_enter ) {
      updateField( 'ENTER', id, data.Name, { Content: validation_result.content } );
      return { status: SUCCESS_CODE, content: validation_result.content };
    }
    
    if ( is_action_tab ) {
      updateField( 'TAB', id, data.Name, { Content: validation_result.content } );
      return { status: SUCCESS_CODE, content: validation_result.content };
    }
  }

  //#endregion

  //#region [METHOD]

  //-----------------------------------------------------------------
  function getInputData() {
    //-----------------------------------------------------------------
    if( data.Id == undefined ) return null;

    let content = data.Entries ? data.Entries[0].Content : '';
    const STANDARD_PROPS = {
      name       : 'field_txt'   ,
      id         : id            ,
      style      : _styles.input ,
      inputStyle : _styles.input_content,
      field      : data          ,
      value      : content       ,
      has_focus  : has_focus     ,
      outer_ref  : ref_outer     ,
      onFocus    : onFocus       ,
      onBlur     : onBlur        ,
      onKeyDown  : onKeyDown     ,
      inputRef   : setRefNode    ,
      setMessage : setMessage    ,
      confirm,
    }

    const type = data.DisplayInfo?.Type;
    if( type === 'pagetype'  ) {
      input_validation = validate_pagetype;
      return <Pagetype  { ...STANDARD_PROPS } />;
    }
    else if( type === 'doctype'   ) {
      input_validation = validate_doctype;
      return <Doctype   { ...STANDARD_PROPS } />;
    }
    else if( type === 'selection' ) {
      input_validation = validate_selection;
      return <Selection { ...STANDARD_PROPS } />;
    }
    else if( type === 'amount'    ) {
      input_validation = validate_amount;
      return <Amount    { ...STANDARD_PROPS } />;
    }
    else if( type === 'amount'    ) {
      input_validation = validate_integer;
      return <Integer    { ...STANDARD_PROPS } />;
    }
    else if( type === 'date'      ) {
      input_validation = validate_date;
      return <Date      { ...STANDARD_PROPS } />;
    }
    else if( type === 'min'      ) {
      input_validation = validate_min;
      return <MIN       { ...STANDARD_PROPS } />;
    }
    else if( selection_values.length > 0 ) {
      input_validation = validate_selection;
      return <Selection { ...STANDARD_PROPS } />;
    }
    else {
      input_validation = validate_standard;
      return <Standard  { ...STANDARD_PROPS } />;
    }
  }

  //-----------------------------------------------------------------
  function createSnippets() {
    //-----------------------------------------------------------------
    try {
      if( !should_create_snippets ) return [];
      if( data == undefined || data.Entries == undefined ) return;

      const results = [];
      for( let entry of data.Entries ) {
        const page_number = parseInt(entry.PageNr);
        const image = images.values().find(x =>
          x.page_number === page_number || x.page_number_original === page_number
        );
        if( !image ) continue;
        // Multi-page fields must use the metadata of each entry's own page.
        const info = image.info || data?.image?.info || {};
        const coords = entryImageRect(entry, info);

        if( coords.width > 12000 || coords.height > 12000 ) {
          continue;
        }
        if( coords.width <= 0 || coords.height <= 0 ) {
          continue;
        }

        const offset_x = 100;
        const offset_y = 100;

        const canvas = document.createElement('canvas');
        const width = coords.width + offset_x;
        const height = coords.height + offset_y;
        const rotation = displayRotation(info);
        const dimensions = rotatedDimensions(width, height, rotation);
        canvas.width = dimensions.width;
        canvas.height = dimensions.height;

        const offset_x_half = offset_x / 2;
        const offset_y_half = offset_y / 2;
        const context = canvas.getContext('2d');
        fillCanvasBackground(context);
        context.save();
        context.translate(canvas.width / 2, canvas.height / 2);
        context.rotate(rotationComponents(rotation).radians);
        context.translate(-width / 2, -height / 2);
        context.drawImage( image.html_image,
          coords.x-offset_x_half, coords.y-offset_y_half, coords.width+offset_x, coords.height+offset_y,
          0, 0, coords.width+offset_x, coords.height+offset_y
        );

        const color = true ? 'rgb( 255, 255, 0 )' : 'rgb( 128, 255, 0 )'

        context.globalCompositeOperation = "darken";

        context.beginPath();

        context.fillStyle = color;
        context.fillRect(offset_x_half, offset_y_half, coords.width, coords.height);

        context.closePath();
        context.restore();

        results.push( canvas );
      }

      return results;

    } catch (error) {
      console.warn( {error} )
    }

    return [];
  }

  //#endregion

  function validate_common (content) {
    const result = {
      status  : SUCCESS_CODE,
      message : '',
    }

    // LenOption
    const lenOpt = data?.DisplayInfo?.LenOption?.toLowerCase();
    if(lenOpt) {
      // Strict Len - content must match field length exactly
      if(lenOpt.includes('s') && content.length != length_char) {
        result.status  = FAILURE_CODE;
        result.message = `Invalid field length, must be exactly ${length_char} characters`;
      }
      // Max Len - content length must not exceed field length
      else if(lenOpt.includes('f') && content.length > length_char) {
        result.status  = FAILURE_CODE;
        result.message = `Invalid field length, Max ${length_char} characters`;
      }
    }

    return result;
  }

  function getFocusMessage(field) {
    const display_info = field?.DisplayInfo || {};
    return [display_info.Message, display_info.InputMessage, display_info.HelpText]
      .filter(Boolean)
      .join('\n') || '';
  }

  //#region [RENDER]
 
  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    const input_element = data.Id ? getInputData() : null;

    return (
      <Box key={id} ref={ref_outer} { ..._props.container }>
        {
          data.Id == undefined
          ?
            is_in_view ? <div>Loading...</div> : null
          :
            <>
              <Box sx={_styles.header_container}>
                <CardHeader { ..._props.header  } action={displayPageNumber()}/>
              </Box>
              <Box { ..._props.content } >
                { input_element }
                { has_focus && message && (
                  <Typography variant="body2" sx={{ marginTop: '4px', opacity: 0.8, whiteSpace: 'pre-line' }} >
                    {message}
                  </Typography>
                )}
              </Box>
              <Spacer size='0.5rem' />
              {displaySnippets()}
            </>
        }
      </Box>
    )
  }

  //-----------------------------------------------------------------
  function displaySnippets() {
    //-----------------------------------------------------------------
    if( !snippets || snippets.length <= 0 || !images ) return null;
    if( !setting_snippets_enabled ) return null;

    return (
      <div style={{
        display    : 'inline flex' ,
        flexFlow   : 'row no-wrap' ,
        flexWrap   : 'wrap', }}>
        { snippets.map( ( snippet, index ) => displaySnippet( snippet, index ) ) }
      </div>
    )
  }

  //-----------------------------------------------------------------
  function displaySnippet(snippet, index) {
    //-----------------------------------------------------------------
    if( !snippet || !images ) return null;

    return (
      <BorderCorners key={index} color={theme.palette.primary.main} width={snippet?.width} height={snippet?.height} thickness='3px' animate={setting_snippets_animate && !is_validated} is_open={has_focus} topLeft={false} bottomRight={false}>
        <canvas ref={ (ref)=>drawCanvas(ref, snippet) }/>
      </BorderCorners>
    )
  }

  //-----------------------------------------------------------------
  function drawCanvas( ref, snippet ) {
    //-----------------------------------------------------------------
    if( !ref || !snippet ) return;
    ref.width  = snippet.width ; 
    ref.height = snippet.height;
    const context = ref.getContext('2d');
    context.drawImage( snippet, 0, 0 );
  }

  //-----------------------------------------------------------------
  function displayPageNumber() {
    //-----------------------------------------------------------------
    if( !data.page_number ) return null;

    return <Box sx={_styles.page_number}>Page: {data.page_number}</Box>
  }

  //#endregion

  return display();
}
